/**
 * Geometry of the Shipcast mark, traced from the approved (AI-generated, raster)
 * reference artwork in its own pixel units: 1 unit = 1 px of the 1774-px-wide
 * reference sheet, origin at its crop (160, 40). The mark spans x 14..744,
 * y 4..268; the baseline every lane settles onto is y 265.
 *
 * `compact` keeps the dots and lane starts (x < LANES_FROM) as they are,
 * shortens the straight run of the lanes hard and the bell only a little —
 * squeezing evenly turns the bell into a spike.
 */
export const MARK_LEFT = 14;
export const MARK_TOP = 4;
export const MARK_HEIGHT = 264;
const MARK_RIGHT = 744;
const LANES_FROM = 140;
const BELL_FROM = 370;
const COMPACT_LANE_SCALE = 0.25;
const COMPACT_BELL_SCALE = 0.65;

export const DOT_RADIUS = 27;
/** Radius of the concave notch each bar starts with: a 6-unit gap around its dot. */
export const NOTCH_RADIUS = 33;
/** Dot centres, top lane first; the dots never move under `compact`. */
export const DOTS = [
  { cx: 42, cy: 93 },
  { cx: 72, cy: 158 },
  { cx: 107, cy: 220 },
] as const;

export function markGeometry(compact: boolean) {
  const laneScale = compact ? COMPACT_LANE_SCALE : 1;
  const bellScale = compact ? COMPACT_BELL_SCALE : 1;
  const bellStart = LANES_FROM + (BELL_FROM - LANES_FROM) * laneScale;
  const squeezed = (value: number) => {
    if (value <= LANES_FROM) return value;
    if (value <= BELL_FROM) return LANES_FROM + (value - LANES_FROM) * laneScale;
    return bellStart + (value - BELL_FROM) * bellScale;
  };
  const x = (value: number) => Math.round(squeezed(value) * 100) / 100;
  /** One "x y" point, with x squeezed. */
  const p = (px: number, py: number) => `${x(px)} ${py}`;
  /** Gradient offset of a reference x, over the shared 0..right axis. */
  const at = (value: number) => Math.round((x(value) / x(MARK_RIGHT)) * 1000) / 1000;
  const peakX = x(538);
  /** From the lane's top edge at x 370, up over the peak and down the flank to (620, 118). */
  const bellTop = `V73 C${p(395, 73)} ${p(410, 66)} ${p(440, 60)} C${p(472, 52)} ${p(500, 24)} ${p(538, 13)} C${p(566, 3)} ${p(582, 42)} ${p(600, 80)} C${p(608, 96)} ${p(615, 108)} ${p(620, 118)}`;

  return {
    right: x(MARK_RIGHT),
    peakX,
    /** Pale distribution of likely dates: the whole bell silhouette, behind every lane. */
    body: `M${p(290, 113)} H${x(370)} ${bellTop} C${p(640, 160)} ${p(665, 215)} ${p(700, 245)} C${p(715, 256)} ${p(728, 262)} ${p(742, 265)} H${x(600)} C${p(520, 263)} ${p(470, 250)} ${p(420, 245)} C${p(330, 240)} ${p(295, 200)} ${p(290, 110)} Z`,
    /** Longest lane: a bar that rises into the bell. Starts under its dot; the notch mask cuts it. */
    top: `M${p(42, 73)} H${x(370)} ${bellTop} C${p(580, 119)} ${p(470, 115)} ${p(420, 113)} H${x(42)} Z`,
    /** Middle lane: a ribbon that bends down beneath the bell and thins into the baseline. */
    middle: `M${p(72, 138)} H${x(360)} C${p(400, 138)} ${p(440, 162)} ${p(500, 200)} C${p(560, 238)} ${p(640, 262)} ${p(742, 265)} C${p(620, 264)} ${p(540, 250)} ${p(475, 225)} C${p(440, 210)} ${p(415, 182)} ${p(380, 178)} H${x(72)} Z`,
    /** Shortest lane: a pill that fades out… */
    cyanBar: `M${p(107, 200)} H${x(300)} A20 20 0 0 1 ${p(300, 240)} H${x(107)} Z`,
    /** …and resurfaces as a wave under the middle lane. */
    cyanWave: `M${p(320, 205)} C${p(370, 205)} ${p(410, 195)} ${p(450, 200)} C${p(520, 215)} ${p(600, 250)} ${p(742, 265)} H${x(600)} C${p(540, 262)} ${p(500, 250)} ${p(460, 246)} C${p(420, 242)} ${p(380, 240)} ${p(320, 238)} Z`,
    /**
     * Mask shape for the top lane, blurred: its lower edge sits ~20 units above
     * the lane's bottom inside the bell, so after the blur the violet has faded
     * out before its geometric edge instead of ending on a hard line. It drops
     * well below the bar further left, where the bar's own edge must stay crisp.
     */
    topFade: `M${p(400, -40)} H${x(MARK_RIGHT + 40)} V98 H${x(540)} C${p(495, 98)} ${p(470, 140)} ${p(430, 145)} H${x(400)} Z`,
    /** Left of this x the top lane is not softened at all. */
    topFadeFrom: x(440),
    /** The forecast line through the peak. */
    line: `M${peakX} 14 V265`,
    /** Gradient stop offsets, at the same reference x in both variants. */
    stops: {
      bodyStart: at(290),
      bodyMid: at(420),
      bell: at(538),
      bellEdge: at(620),
      laneBend: at(370),
      middleSolid: at(384),
      middleFade: at(460),
      cyanBarSolid: at(250),
      cyanBarEnd: at(320),
      waveStart: at(340),
      waveIn: at(430),
      waveOut: at(500),
      waveFade: at(560),
    },
    lineWidth: compact ? 6 : 2.5,
    dash: compact ? '16 10' : '10 7',
  };
}
